<template>
  <div
    class="w-full fixed top-0 left-0 right-0 bottom-0 bg-[#FAFAFA] flex flex-col z-100"
  >
    <div
      class="w-full min-h-[60px] max-h-[60px] flex items-center justify-between tablet-h:justify-center bg-white p-3 tablet-h:px-[18px] tablet-h:py-3 border-b border-[#E5E5E5]"
    >
      <div class="hidden tablet-h:block w-1/3">
        <div class="flex items-center">
          <Button
            v-if="showDownloadButton"
            :icon="DownloadIcon"
            variant="primary"
            class="mr-5"
            @click="handleDownloadClick"
          >
            Download
          </Button>
          <Button :icon="ShareIcon" variant="stroke" @click="handleShareClick">
            Share
          </Button>
        </div>
      </div>
      <div class="w-1/3 flex tablet-h:justify-center">
        <DacrIcon class="h-[30px] w-auto" />
      </div>
      <div class="w-1/3 flex items-center justify-end">
        <Button
          v-if="isOwner"
          class="hidden tablet-h:flex mr-5"
          :icon="GearIcon"
          variant="stroke"
          @click="handleFileSettingsClick"
        >
          Copyright Settings
        </Button>
        <Button
          :icon="CloseIcon"
          variant="ghost"
          aria-label="Close preview"
          @click="handleClose"
        />
      </div>
    </div>
    <div class="h-full p-3 tablet-h:p-10 overflow-y-auto">
      <div
        v-if="isStaticPreview"
        class="w-full h-full flex items-center justify-center"
      >
        <div
          class="h-full max-h-[1000px] aspect-square max-w-full flex items-center justify-center"
        >
          <Thumbnail
            :src="previewImageUrl"
            :type="thumbnailType"
            :alt="props.file.name"
          />
        </div>
      </div>
      <div
        v-else-if="isPagesPreview"
        class="flex flex-col items-center relative"
      >
        <img
          v-for="index in totalPages"
          :key="index"
          :src="getPageUrl(index)"
          loading="lazy"
          class="max-w-[800px] w-full h-full mb-10 last:mb-0 pointer-events-none select-none"
        />
      </div>
    </div>
    <div
      class="tablet-h:hidden w-full min-h-[60px] max-h-[60px] flex items-center justify-between bg-white px-3 border-t border-[#E5E5E5]"
    >
      <div class="flex items-center">
        <Button
          v-if="showDownloadButton"
          variant="primary"
          :icon="DownloadIcon"
          class="mr-1.5"
          @click="handleDownloadClick"
        >
          Download
        </Button>
        <Button :icon="ShareIcon" variant="stroke" @click="handleShareClick">
          Share
        </Button>
      </div>
      <Button
        v-if="isOwner"
        :icon="GearIcon"
        variant="stroke"
        @click="handleFileSettingsClick"
      >
        <span v-if="isViewportWideEnough" class="font-secondary">
          Copyright settings
        </span>
      </Button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { FilePermission, FileRole } from '@/shared/graphql/router/graphql'
import type { CopyrightFile } from '@/features/copyright/composables/useCopyrightFile'
import { useDevice } from '@/shared/composables/useDevice'
import { computed, onBeforeUnmount, onMounted } from 'vue'
import { openModal, Modals } from '@/shared/dialogs/modals'
import { openDialog, Dialog } from '@/shared/dialogs/dialog'
import { Button, Thumbnail } from '@dacr/library'
import {
  CloseIcon,
  DacrIcon,
  DownloadIcon,
  GearIcon,
  ShareIcon,
} from '@dacr/icons'
import { applyAuthorizationToken } from '@/shared/utils/url'
import { fileTypeToThumbnail } from '@/shared/utils/thumbnail'
import padStart from 'lodash.padstart'

const props = defineProps<{
  file: CopyrightFile
}>()
const emit = defineEmits<{
  closeViewer: []
}>()

const device = useDevice()

onMounted(() => {
  window.addEventListener('keydown', handleKeydown)
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleKeydown)
})

const previewImageUrl = computed(() =>
  props.file.asset?.previewImageUrl
    ? applyAuthorizationToken(props.file.asset.previewImageUrl)
    : undefined
)

const thumbnailType = computed(() => fileTypeToThumbnail(props.file.type))

const isStaticPreview = computed(
  () => props.file.asset?.__typename !== 'DocumentAsset'
)

const isPagesPreview = computed(
  () => props.file.asset?.__typename === 'DocumentAsset'
)

const totalPages = computed(() =>
  props.file.asset?.__typename === 'DocumentAsset'
    ? props.file.asset.totalPages
    : undefined
)

const documentUrl = computed(() =>
  props.file.asset?.__typename === 'DocumentAsset'
    ? props.file.asset.documentUrl
    : undefined
)

const isOwner = computed(() => props.file.role === FileRole.Owner)

const isViewportWideEnough = computed(() => device.viewportWidth.value >= 428)

const showDownloadButton = computed(() =>
  props.file.permissions.includes(FilePermission.Download)
)

const handleClose = () => {
  emit('closeViewer')
}

const handleKeydown = (event: KeyboardEvent) => {
  if (event.code === 'Escape') {
    handleClose()
  }
}

const getPageUrl = (index: number) => {
  const url = documentUrl.value
  if (!url) return ''

  const padding = totalPages.value?.toString().length
  const pageString = padStart(index.toString(), padding, '0')

  return (
    applyAuthorizationToken(url.replace(/-(.*)\./, `-${pageString}.`)) ?? ''
  )
}

const handleFileSettingsClick = () => {
  openDialog(Dialog.FileSettings, {
    props: { file: props.file },
  })
}

const handleShareClick = () => {
  openDialog(Dialog.Share, {
    props: { file: props.file },
  })
}

const handleDownloadClick = () => {
  openModal(Modals.Download, {
    props: { file: props.file },
  })
}
</script>
